import projectKeys from '@/api/queries/project/keyFactory';
import timelogKeys from '@/api/queries/timelog/keyFactor';
import { Alert, Button, Modal, ModalProps } from '@payaca/ambrosia';

import { archiveTimelog } from '@payaca/store/timelogs/timelogsActions';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

export type TProps = Pick<ModalProps, 'isOpen' | 'onClose'> & {
  timelogId?: string;
  // Optional: only present when the modal is mounted from a project's Labour
  // tab, where the project's profit breakdown also needs invalidating. On the
  // account-wide timelogs reporting page there's no single project to refresh.
  projectId?: string;
};

const DeleteTimelogModal: FC<TProps> = (props) => {
  const { isOpen, onClose, timelogId, projectId } = props;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isDeletingTimelog, setIsDeletingTimelog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = () => {
    setErrorMessage(null);
    onClose?.();
  };

  const handleDeleteTimelog = async () => {
    if (!timelogId) {
      return;
    }

    setErrorMessage(null);
    setIsDeletingTimelog(true);

    new Promise<void>((resolve, reject) => {
      dispatch(
        archiveTimelog.request({
          timelogPublicId: timelogId,
          callback: () => resolve(),
          onErrorCallback: (error) => reject(error),
        })
      );
    })
      .then(() => {
        void queryClient.invalidateQueries({
          queryKey: timelogKeys.list(),
        });
        if (projectId) {
          void queryClient.invalidateQueries({
            queryKey: projectKeys.profitBreakdown(projectId),
          });
        }

        onClose?.();
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage('Failed to delete timelog. Please try again.');
      })
      .finally(() => {
        setIsDeletingTimelog(false);
      });
  };

  return (
    <Modal title="Delete timelog" isOpen={isOpen} onClose={handleClose}>
      <Modal.Body className="space-y-4">
        <p>Are you sure you want to delete this timelog?</p>
        {errorMessage && <Alert colour="red">{errorMessage}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" onClick={handleClose}>
            Keep
          </Button>
          <Button
            colour="negative"
            disabled={isDeletingTimelog}
            isProcessing={isDeletingTimelog}
            onClick={handleDeleteTimelog}
          >
            Delete
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteTimelogModal;
